/**
 * The three derived tables of the Umpire Appointments page — §B6's matrix, §B7's teams
 * coverage and §B8's per-umpire summary — computed twice each, once per dataset.
 *
 * **Derived live, never stored** (Part C invariant 1), exactly as the technical page's
 * `../appointments/derive.ts` is, and for the same reason: a filter must reshape a total
 * correctly and a stale count must never survive a change.
 *
 * **Two datasets that never meet in one array** (Part C invariant 5). Every function here
 * takes the dataset by name and reads `fixture.tmsActual` or `fixture.aiReference` through
 * one accessor. Nothing concatenates them, nothing sums across them, and the only place the
 * two are ever put beside each other is §B9's evenness note — which prints two measurements
 * side by side rather than merging them into one.
 *
 * **No combined total, anywhere** (§B6). `UmpireMatrixRow` carries three duty counts and no
 * fourth field, and `UmpireSummaryRow` the same: on-field, reserve and video are three
 * ledgers, and a type that could hold their sum is a type somebody would eventually print.
 */

import type { UmpireSlot } from "@hockeytd/shared";

import type { UmpireFixtureBody, UmpireSlotBody } from "../../api/types.js";
import type { OfficialView } from "../../api/types.js";
import type { CompetitionFilter, StageFilter } from "../appointments/derive.js";

/** Which half of the page a table is measuring. Named at every call, never defaulted. */
export type UmpireDataset = "tmsActual" | "aiReference";

/** §B8's own filter — gender, and nothing else. */
export type UmpireGenderFilter = "all" | "m" | "f";

/** The one accessor. Both halves are reached through it, so neither can be reached by accident. */
export function slotsOf(fixture: UmpireFixtureBody, dataset: UmpireDataset): UmpireSlotBody[] {
  return dataset === "tmsActual" ? fixture.tmsActual : fixture.aiReference;
}

/** Whether a fixture belongs to the pool stage — the same test the technical page makes. */
export function isPoolFixture(fixture: UmpireFixtureBody): boolean {
  return fixture.stage.trim().toLowerCase().startsWith("pool");
}

export function filterUmpireFixtures(
  fixtures: readonly UmpireFixtureBody[],
  competition: CompetitionFilter,
  stage: StageFilter = "all",
): UmpireFixtureBody[] {
  return fixtures.filter((fixture) => {
    if (competition !== "all" && fixture.competitionGender !== competition) return false;
    if (stage === "pool") return isPoolFixture(fixture);
    if (stage === "post") return !isPoolFixture(fixture);
    return true;
  });
}

/* ── Who the derived tables have rows for ─────────────────────────────────── */

/**
 * One umpire as §B6, §B7 and §B8 name them: the surname their columns are headed with, the
 * country, the `NEU` / `AHF` panel chip and the gender the name colour is taken from.
 *
 * `avoidVu` travels with the row because §B8's Video column marks it: an umpire who will
 * never be drafted as VU is at zero for a reason, and a zero with no explanation reads as an
 * oversight by the Umpires Managers rather than as the TD's own instruction.
 */
export interface UmpirePanelMember {
  tmsPersonId: string;
  surname: string;
  mediaName: string;
  country: string;
  panel: "Neutral" | "AHF" | null;
  gender: "m" | "f" | null;
  avoidVu: boolean;
}

/** Surname-first, as a media name is written: `OKONKWO Ada` → `OKONKWO`. */
export function surnameOf(mediaName: string): string {
  const [first] = mediaName.trim().split(/\s+/);
  return first ?? mediaName;
}

/**
 * Everybody the derived tables carry a row for, in a stable order: the umpiring panel from
 * the roster first — so an umpire with no duty yet is visibly at zero rather than absent,
 * which is how the TD sees who is being left out — then anybody appointed in **this dataset**
 * whom the roster does not carry, appended rather than dropped.
 */
export function umpirePanelMembers(
  fixtures: readonly UmpireFixtureBody[],
  officials: readonly OfficialView[],
  dataset: UmpireDataset,
): UmpirePanelMember[] {
  const rows: UmpirePanelMember[] = [];
  const seen = new Set<string>();

  for (const official of officials) {
    if (official.role !== "Umpire") continue;
    seen.add(official.tmsPersonId);
    rows.push({
      tmsPersonId: official.tmsPersonId,
      surname: official.surnames.toUpperCase(),
      mediaName: official.mediaName,
      country: official.country,
      panel: official.panel,
      gender: official.gender,
      avoidVu: official.avoidVu,
    });
  }

  for (const fixture of fixtures) {
    for (const slot of slotsOf(fixture, dataset)) {
      if (slot.tmsPersonId === null || seen.has(slot.tmsPersonId)) continue;
      seen.add(slot.tmsPersonId);
      rows.push({
        tmsPersonId: slot.tmsPersonId,
        surname: surnameOf(slot.mediaName ?? slot.tmsPersonId),
        mediaName: slot.mediaName ?? slot.tmsPersonId,
        country: slot.country ?? "—",
        panel: slot.panel,
        gender: null,
        avoidVu: false,
      });
    }
  }

  return rows;
}

export function filterByGender(
  members: readonly UmpirePanelMember[],
  gender: UmpireGenderFilter,
): UmpirePanelMember[] {
  return members.filter((member) => gender === "all" || member.gender === gender);
}

/* ── §B6 — duties by role, and who worked with whom ───────────────────────── */

/** Which of §B8's three ledgers a slot belongs to. U1 and U2 are one figure (§B6). */
export function ledgerOf(slot: UmpireSlot): "onField" | "reserve" | "video" {
  if (slot === "U1" || slot === "U2") return "onField";
  return slot === "VU" ? "video" : "reserve";
}

/**
 * RUN 81 — which appointments `Appointed Together With` counts: §B6a's `On Field Only`.
 *
 * The Technical Delegate asked for it on 15 September 2026, after live-testing this page:
 * *"When ON, the 'Appointed Together With' matrix counts only matches where BOTH umpires held
 * on-field duties (U1/U2 — the on-pitch pair). Reserve and video co-appointments are excluded
 * from the cell counts and the density shading."* What he is reading is §B5's partner
 * rotation — who has stood on the pitch with whom — and the four-slot count folds the reserve
 * and the video umpire in beside every pair, which is the noise he asked to take out.
 *
 * `all` is §B6 exactly as it has always been. It is an axis of its own rather than another tab
 * of the competition filter because the two combine: a Women's matrix can be read either way.
 */
export type UmpireTogetherFilter = "all" | "onField";

export interface UmpireMatrixRow {
  umpire: UmpirePanelMember;
  /** U1 + U2 (§B6 `On-Field`). */
  onField: number;
  /** RU. */
  reserve: number;
  /** VU. */
  video: number;
  /**
   * `Appointed Together With` — co-appointments, keyed by the other umpire's TMS person id.
   *
   * Across all four slots by default (§B6). RUN 81 — under `On Field Only` (`together:
   * "onField"`) only the U1 and U2 holders of a match count with each other: the pair on the
   * pitch, and nobody in the reserve's seat or at the video screen beside it.
   */
  co: Record<string, number>;
  /** `co` summed across the other umpires, under the same filter as the cells. */
  coTotal: number;
}

export interface UmpireMatrixTable {
  rows: UmpireMatrixRow[];
  /**
   * Three column sums and the co-appointment total. **There is no role total** (§B6).
   *
   * The three duty sums never depend on `On Field Only`; `coTotal` always does, exactly as the
   * cells it adds up do.
   */
  totals: { onField: number; reserve: number; video: number; coTotal: number };
  /** Each umpire's column of `co`, summed — under the same filter as the cells. */
  coColumnTotals: Record<string, number>;
  fixtures: number;
}

/**
 * §B6's matrix for one dataset.
 *
 * **`Duties By Role` counts every held slot, whatever `together` says.** `On Field Only` is a
 * question about pairings, and an umpire's reserve and video duties are still his duties: a
 * left-hand block that read differently under the toggle would be the toggle answering a
 * question nobody asked it.
 *
 * **`Appointed Together With` counts the seats `together` names.** Under `all`, everybody
 * holding any of the four slots of a match counts once with everybody else holding one (§B6).
 * Under `onField` the only seats that count are U1 and U2, so a match adds exactly one pair —
 * once in each direction — and only when both seats are held. An unfilled seat pairs nobody,
 * and one person holding both seats (a `Two Roles In One Match` violation) is never counted as
 * his own partner, by the same `one === other` rule that keeps the diagonal at `×`.
 *
 * `coTotal`, `coColumnTotals` and `totals.coTotal` are summed from the filtered cells, so the
 * totals — and the heat shading drawn from those same cells — can never describe a different
 * count from the one printed.
 */
export function buildUmpireMatrix(
  fixtures: readonly UmpireFixtureBody[],
  umpires: readonly UmpirePanelMember[],
  dataset: UmpireDataset,
  together: UmpireTogetherFilter = "all",
): UmpireMatrixTable {
  const index = new Map(umpires.map((umpire, position) => [umpire.tmsPersonId, position]));
  const rows: UmpireMatrixRow[] = umpires.map((umpire) => ({
    umpire,
    onField: 0,
    reserve: 0,
    video: 0,
    co: {},
    coTotal: 0,
  }));

  for (const fixture of fixtures) {
    // Who counts towards `Appointed Together With` in this match: every held seat, or under
    // `On Field Only` the held U1 and U2 seats alone. The duty ledgers below count every seat
    // either way.
    const present: string[] = [];
    for (const slot of slotsOf(fixture, dataset)) {
      if (slot.tmsPersonId === null) continue;
      if (together === "all" || ledgerOf(slot.slot) === "onField") present.push(slot.tmsPersonId);
      const position = index.get(slot.tmsPersonId);
      if (position === undefined) continue;
      const row = rows[position];
      if (row === undefined) continue;
      row[ledgerOf(slot.slot)] += 1;
    }
    for (const one of present) {
      for (const other of present) {
        if (one === other) continue;
        const position = index.get(one);
        if (position === undefined) continue;
        const row = rows[position];
        if (row === undefined) continue;
        row.co[other] = (row.co[other] ?? 0) + 1;
      }
    }
  }

  const totals = { onField: 0, reserve: 0, video: 0, coTotal: 0 };
  const coColumnTotals: Record<string, number> = {};
  for (const umpire of umpires) coColumnTotals[umpire.tmsPersonId] = 0;

  for (const row of rows) {
    totals.onField += row.onField;
    totals.reserve += row.reserve;
    totals.video += row.video;
    let coTotal = 0;
    for (const umpire of umpires) {
      if (umpire.tmsPersonId === row.umpire.tmsPersonId) continue;
      const value = row.co[umpire.tmsPersonId] ?? 0;
      coTotal += value;
      coColumnTotals[umpire.tmsPersonId] = (coColumnTotals[umpire.tmsPersonId] ?? 0) + value;
    }
    row.coTotal = coTotal;
    totals.coTotal += coTotal;
  }

  return { rows, totals, coColumnTotals, fixtures: fixtures.length };
}

/* ── §B7 — teams coverage ─────────────────────────────────────────────────── */

export interface UmpireTeamsRow {
  umpire: UmpirePanelMember;
  per: Record<string, number>;
  total: number;
}

export interface UmpireTeamsTable {
  teams: string[];
  rows: UmpireTeamsRow[];
  columnTotals: Record<string, number>;
  grandTotal: number;
  fixtures: number;
}

export function buildUmpireTeams(
  fixtures: readonly UmpireFixtureBody[],
  umpires: readonly UmpirePanelMember[],
  dataset: UmpireDataset,
): UmpireTeamsTable {
  const index = new Map(umpires.map((umpire, position) => [umpire.tmsPersonId, position]));
  const teamSet = new Set<string>();
  const rows: UmpireTeamsRow[] = umpires.map((umpire) => ({ umpire, per: {}, total: 0 }));

  for (const fixture of fixtures) {
    const teams = [fixture.homeTeamCode, fixture.awayTeamCode].filter(
      (team): team is string => team !== null,
    );
    for (const team of teams) teamSet.add(team);
    for (const slot of slotsOf(fixture, dataset)) {
      if (slot.tmsPersonId === null) continue;
      const position = index.get(slot.tmsPersonId);
      if (position === undefined) continue;
      const row = rows[position];
      if (row === undefined) continue;
      for (const team of teams) {
        row.per[team] = (row.per[team] ?? 0) + 1;
        row.total += 1;
      }
    }
  }

  const teams = [...teamSet].sort();
  const columnTotals: Record<string, number> = {};
  let grandTotal = 0;
  for (const team of teams) {
    let sum = 0;
    for (const row of rows) sum += row.per[team] ?? 0;
    columnTotals[team] = sum;
    grandTotal += sum;
  }

  return { teams, rows, columnTotals, grandTotal, fixtures: fixtures.length };
}

/* ── §B8 — the per-umpire summary, three figures and never one ────────────── */

/** One ledger as §B8 prints it: `completed / appointed`. */
export interface UmpireCount {
  completed: number;
  appointed: number;
}

export interface UmpireSummaryRow {
  umpire: UmpirePanelMember;
  onField: UmpireCount;
  video: UmpireCount;
  reserve: UmpireCount;
}

/**
 * §B8's table. **Completed is measured, not assumed** — a match counts as completed only
 * where the schedule's own TMS status says it has been played, which is why `completed`
 * arrives as a set of match ids from `GET /fixtures` rather than being inferred from a clock.
 * With no schedule loaded every completed figure is zero and the panel foot says why.
 *
 * Sorted by on-field appointed, then on-field completed (§B8), then surname so the order is
 * stable rather than incidental.
 */
export function buildUmpireSummary(
  fixtures: readonly UmpireFixtureBody[],
  umpires: readonly UmpirePanelMember[],
  dataset: UmpireDataset,
  completedMatchIds: ReadonlySet<number>,
): UmpireSummaryRow[] {
  const index = new Map(umpires.map((umpire, position) => [umpire.tmsPersonId, position]));
  const rows: UmpireSummaryRow[] = umpires.map((umpire) => ({
    umpire,
    onField: { completed: 0, appointed: 0 },
    video: { completed: 0, appointed: 0 },
    reserve: { completed: 0, appointed: 0 },
  }));

  for (const fixture of fixtures) {
    const played = completedMatchIds.has(fixture.tmsMatchDbId);
    for (const slot of slotsOf(fixture, dataset)) {
      if (slot.tmsPersonId === null) continue;
      const position = index.get(slot.tmsPersonId);
      if (position === undefined) continue;
      const row = rows[position];
      if (row === undefined) continue;
      const count = row[ledgerOf(slot.slot)];
      count.appointed += 1;
      if (played) count.completed += 1;
    }
  }

  return rows.sort(
    (a, b) =>
      b.onField.appointed - a.onField.appointed ||
      b.onField.completed - a.onField.completed ||
      (a.umpire.surname < b.umpire.surname ? -1 : a.umpire.surname > b.umpire.surname ? 1 : 0),
  );
}

/* ── Heat and the flash keys ──────────────────────────────────────────────── */

/** §B6's thresholds, identical to the technical page's so the two read the same way. */
export function heatClass(value: number): string {
  if (value >= 3) return "h3";
  if (value === 2) return "h2";
  if (value === 1) return "h1";
  return "c-zero";
}

/**
 * Every derived cell of one dataset, as `key → value`, for the change flash.
 *
 * The keys carry the dataset name because the same umpire has a row in all six tables: a
 * change on the advisory side must light an advisory cell, never the TMS cell beside it that
 * happens to hold the same number.
 */
export function umpireDerivedCellValues(
  dataset: UmpireDataset,
  matrix: UmpireMatrixTable,
  teams: UmpireTeamsTable,
  summary: readonly UmpireSummaryRow[],
): Map<string, number> {
  const values = new Map<string, number>();
  const prefix = dataset === "tmsActual" ? "a" : "r";

  for (const row of matrix.rows) {
    const id = row.umpire.tmsPersonId;
    values.set(`${prefix}m:${id}:on`, row.onField);
    values.set(`${prefix}m:${id}:ru`, row.reserve);
    values.set(`${prefix}m:${id}:vu`, row.video);
    values.set(`${prefix}m:${id}:cot`, row.coTotal);
    for (const other of matrix.rows) {
      const otherId = other.umpire.tmsPersonId;
      if (otherId === id) continue;
      values.set(`${prefix}m:${id}:${otherId}`, row.co[otherId] ?? 0);
    }
  }

  for (const row of teams.rows) {
    const id = row.umpire.tmsPersonId;
    for (const team of teams.teams) values.set(`${prefix}f:${id}:${team}`, row.per[team] ?? 0);
    values.set(`${prefix}f:${id}:tot`, row.total);
  }

  for (const row of summary) {
    const id = row.umpire.tmsPersonId;
    values.set(`${prefix}s:${id}:on`, row.onField.appointed);
    values.set(`${prefix}s:${id}:vu`, row.video.appointed);
    values.set(`${prefix}s:${id}:ru`, row.reserve.appointed);
  }

  return values;
}
